import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ChooseEntityDialogControlled } from "@/components/ChooseEntityDialog";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import { Entity, EntityType, getEntityNumericId } from "@/queries";
import { useGetInputVariableSet } from "@/queries/input-variable-sets";
import { displayErrorToast, displayToast } from "@/utils/toast";

interface LibraryImportProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onVariablesFetched?: (variables: Record<string, string>, setName?: string) => void;
}

export const LibraryImport = ({ isOpen, onOpenChange, onVariablesFetched }: LibraryImportProps) => {
  const auth = useAuth();
  const { activeWorkspaceId } = useUser();
  const [libraryEntityId, setLibraryEntityId] = useState<number | null>(null);

  const shouldEnableQuery =
    !!libraryEntityId && libraryEntityId > 0 && !!activeWorkspaceId && !!auth?.userToken;

  const inputVariableSetRequest = useGetInputVariableSet(
    activeWorkspaceId!,
    libraryEntityId || 0,
    auth?.userToken ?? "",
    shouldEnableQuery,
  );

  useEffect(() => {
    const inputVariableSet = inputVariableSetRequest.data?.input_variable_set;
    const data = inputVariableSet?.variables as Record<string, string> | undefined;
    const setName = inputVariableSet?.name;
    const { isFetching } = inputVariableSetRequest;

    if (data && !isFetching && libraryEntityId) {
      if (Object.keys(data).length > 0) {
        onVariablesFetched?.(data, setName);
        displayToast("Variables imported successfully!", ToastType.success);
      } else {
        displayErrorToast("No input variables found");
      }
      setLibraryEntityId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputVariableSetRequest.data, inputVariableSetRequest.isFetching, libraryEntityId]);

  useEffect(() => {
    if (inputVariableSetRequest.error && libraryEntityId) {
      displayErrorToast("Failed to fetch variables from library");
      setLibraryEntityId(null);
    }
  }, [inputVariableSetRequest.error, libraryEntityId]);

  const handleSelect = (entity: Entity) => {
    onOpenChange(false);
    setLibraryEntityId(getEntityNumericId(entity.id) ?? null);
  };

  const { setId } = useParams();

  const disabled = setId
    ? [{ id: Number(setId), type: EntityType.INPUT_VARIABLE_SET } as Entity]
    : [];

  return (
    <ChooseEntityDialogControlled
      type={EntityType.INPUT_VARIABLE_SET}
      open={isOpen}
      disabled={disabled}
      onOpenChange={onOpenChange}
      onSelect={handleSelect}
      currentFolderId={null}
    />
  );
};
